import { ApiError } from './apiError';
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
} from './authToken';

// Mirror of apps/frontend/src/lib/apiFetch.ts. See that file for full
// design notes — Phase 2 auth-hardening fetch wrapper with single-flight
// refresh-on-401.

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000';

let inFlightRefresh: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      const r = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) {
        // 429 (rate-limited) and 5xx (server blip) are transient — the refresh
        // cookie may be perfectly valid, so don't tear the session down.
        // Leave the in-memory token in place and let the caller surface the
        // original 401; a later attempt can recover once the limit/outage clears.
        if (r.status === 429 || r.status >= 500) return null;
        clearAccessToken();
        return null;
      }
      const body = (await r.json()) as { accessToken?: string };
      if (!body.accessToken) {
        clearAccessToken();
        return null;
      }
      setAccessToken(body.accessToken);
      return body.accessToken;
    } catch {
      // A network exception is just as transient as a 429/5xx — the refresh
      // cookie may still be valid. Keep the in-memory token rather than tearing
      // the session down; a later attempt recovers once connectivity returns.
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  body?: BodyInit | null | object;
  skipAuth?: boolean;
  skipRefreshOn401?: boolean;
}

function buildInit(token: string | null, options: ApiFetchOptions): RequestInit {
  const headers = new Headers(options.headers ?? {});
  const isFormData = options.body instanceof FormData;
  if (!isFormData && options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token && !options.skipAuth && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  let body: BodyInit | null | undefined;
  if (options.body == null) {
    body = options.body as null | undefined;
  } else if (
    typeof options.body === 'string' ||
    options.body instanceof FormData ||
    options.body instanceof Blob ||
    options.body instanceof URLSearchParams ||
    options.body instanceof ArrayBuffer
  ) {
    body = options.body as BodyInit;
  } else {
    body = JSON.stringify(options.body);
  }

  return {
    ...options,
    headers,
    body,
    credentials: options.credentials ?? 'include',
  };
}

export async function apiFetch(
  path: string,
  options: ApiFetchOptions = {},
): Promise<Response> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const firstAttempt = await fetch(url, buildInit(getAccessToken(), options));
  if (firstAttempt.status !== 401 || options.skipRefreshOn401) {
    return firstAttempt;
  }
  firstAttempt.body?.cancel?.();
  const refreshed = await refreshAccessToken();
  if (!refreshed) {
    return firstAttempt;
  }
  return fetch(url, buildInit(refreshed, options));
}

export async function apiJson<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const res = await apiFetch(path, options);
  if (!res.ok) {
    let message = `Request failed: ${res.status}`;
    let code: string | undefined;
    try {
      const body = (await res.json()) as { message?: string; code?: string };
      if (body.message) message = body.message;
      if (body.code) code = body.code;
    } catch {
      // not JSON
    }
    throw new ApiError(message, res.status, code);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
